import { Alert } from '../../components/atoms/Alert'
import { TableShell } from '../../components/molecules/TableShell'
import { InlineRupeeEdit } from '../../components/molecules/InlineRupeeEdit'
import { Select } from '../../components/atoms/Select'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { Paginator } from '../../components/molecules/Paginator'
import {
  useProductVariantPricing,
  type ProductVariantRowDto
} from '../../../viewmodels/useProductVariantPricing'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /products/variant-pricing — per-product variants with their **selling
 * price** and Healthy/Bleeding status.
 *
 * Each variant row shows Axes · Baseline cost · Selling price (inline ₹
 * edit + Save) · Profit · Margin · Status. The editor is disabled until
 * the variant has a baseline cost (margin/status need it); saving appends
 * a pricing snapshot. (Was "Variants" until 2026-05-30 — the URL + code
 * now match the always-pricing-centric label.)
 */
export function ProductVariantPricingPage(): JSX.Element {
  const {
    products,
    selectedProductId,
    rows,
    total,
    page,
    pageSize,
    isLoading,
    error,
    selectProduct,
    setPage,
    reload,
    setSellingPrice
  } = useProductVariantPricing()
  const { can } = useAuth()
  const canEdit = can(SCREENCODES.PRODUCTVARIANTPRICING, 'edit')

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-h2 text-charcoal">Variant Pricing</h1>
        <div className="w-full sm:w-auto sm:min-w-[18rem] sm:max-w-md">
          <Select
            value={selectedProductId ?? ''}
            onChange={(e) => selectProduct(e.target.value === '' ? null : e.target.value)}
            aria-label="Product"
          >
            <option value="">— Select a product —</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.name} ({p.category_name})</option>
            ))}
          </Select>
        </div>
      </header>

      {error !== null ? (
        <Alert tone="error">
          {error}{' '}
          <button type="button" className="underline underline-offset-2 hover:text-charcoal" onClick={() => reload()}>
            Retry
          </button>
        </Alert>
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center py-12"><Spinner /></div>
      ) : selectedProductId === null ? (
        <Placeholder>Pick a product above to view and price its variants.</Placeholder>
      ) : rows.length === 0 ? (
        <Placeholder>No variants for this product yet. Variants appear here once they are generated.</Placeholder>
      ) : (
        <>
          <VariantsTable rows={rows} canEdit={canEdit} onSave={setSellingPrice} />
          <Paginator page={page} pageSize={pageSize} total={total} onPageChange={setPage} disabled={isLoading} />
        </>
      )}
    </div>
  )
}

function Placeholder({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-body text-body-sm text-text-secondary">{children}</p>
    </div>
  )
}

function VariantsTable({
  rows,
  canEdit,
  onSave
}: {
  readonly rows: ReadonlyArray<ProductVariantRowDto>
  readonly canEdit: boolean
  readonly onSave: (
    variantId: string,
    sellingPricePaisa: number
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
}): JSX.Element {
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Axes</Th>
            <Th>Baseline cost</Th>
            <Th>Selling price</Th>
            <Th>Profit</Th>
            <Th>Margin</Th>
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <VariantRow key={row.variant_id} row={row} canEdit={canEdit} onSave={onSave} />
          ))}
        </tbody>
      </table>
    </TableShell>
  )
}

function VariantRow({
  row,
  canEdit,
  onSave
}: {
  readonly row: ProductVariantRowDto
  readonly canEdit: boolean
  readonly onSave: (
    variantId: string,
    sellingPricePaisa: number
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
}): JSX.Element {
  const hasCost = row.baseline_cost_paisa !== null
  const priced = row.selling_price_paisa !== null
  return (
    <tr className="border-t border-border">
      <Td>
        {row.axes_label !== null ? (
          <span className="font-mono text-text-secondary">{row.axes_label}</span>
        ) : (
          <span className="text-text-secondary">—</span>
        )}
      </Td>
      <Td className="font-mono">
        {hasCost ? formatRupeesDisplay(row.baseline_cost_paisa as number) : <span className="text-text-secondary">—</span>}
      </Td>
      <Td>
        {canEdit && hasCost ? (
          <InlineRupeeEdit
            valuePaisa={row.selling_price_paisa ?? 0}
            onSave={(p) => onSave(row.variant_id, p)}
            ariaLabel="Selling price in rupees"
          />
        ) : priced ? (
          <span className="font-mono">{formatRupeesDisplay(row.selling_price_paisa as number)}</span>
        ) : (
          <span className="text-text-secondary" title={hasCost ? undefined : 'Set the baseline cost first'}>—</span>
        )}
      </Td>
      <Td className="font-mono">
        {row.profit_amount_paisa !== null
          ? formatRupeesDisplay(row.profit_amount_paisa)
          : <span className="text-text-secondary">—</span>}
      </Td>
      <Td className="font-mono">{formatMarginBps(row.margin_bps)}</Td>
      <Td>
        {!priced ? (
          <span className="text-text-secondary">—</span>
        ) : row.is_bleeding ? (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full bg-terra/15 text-terra font-medium text-body-xs"
            title={`margin ${formatMarginBps(row.margin_bps)} < target ${formatMarginBps(row.target_margin_bps)} − tolerance ${formatMarginBps(row.margin_tolerance_bps)}`}
          >
            ⚠ Bleeding
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-forest/15 text-forest font-medium text-body-xs">
            ● Healthy
          </span>
        )}
      </Td>
    </tr>
  )
}

function formatRupeesDisplay(paisa: number): string {
  const sign = paisa < 0 ? '-' : ''
  return `${sign}₹${Math.abs(paisa / 100).toFixed(2)}`
}

function formatMarginBps(bps: number | null): string {
  if (bps === null) return '—'
  return `${(bps / 100).toFixed(2)}%`
}
